// src/features/sheets/utils/formulaEngine.ts
// Formula evaluation engine for spreadsheet - replaces unsafe eval()

import type { CellPosition, CellRange } from "../types";
import {
  colIndexToLetter as colIndexToLetterFn,
  colLetterToIndex as colLetterToIndexFn,
} from "../constants";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

// Internal alias
type CellRef = CellPosition;

export interface FormulaResult {
  value: string | number;
  error?: string;
  isLoading?: boolean;
}

export type CellGetter = (row: number, col: number) => string;

// ─────────────────────────────────────────────────────────────────────────────
// Cell Reference Parsing
// ─────────────────────────────────────────────────────────────────────────────

// Re-export from constants for components that import from here
export const colLetterToIndex = colLetterToIndexFn;
export const colIndexToLetter = colIndexToLetterFn;

/**
 * Parses a cell reference like "A1" or "AB123" into {col, row}
 */
export function parseCellRef(ref: string): CellRef | null {
  const match = ref.match(/^([A-Z]+)(\d+)$/i);
  if (!match || !match[1] || !match[2]) return null;
  return {
    col: colLetterToIndex(match[1].toUpperCase()),
    row: parseInt(match[2], 10) - 1,
  };
}

/**
 * Parses a range like "A1:B5" into start and end refs
 */
export function parseRange(range: string): CellRange | null {
  const parts = range.split(":");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;

  const start = parseCellRef(parts[0]);
  const end = parseCellRef(parts[1]);

  if (!start || !end) return null;
  return { start, end };
}

/**
 * Gets all cell refs in a range
 */
export function getCellsInRange(range: CellRange): CellRef[] {
  const cells: CellRef[] = [];
  const minRow = Math.min(range.start.row, range.end.row);
  const maxRow = Math.max(range.start.row, range.end.row);
  const minCol = Math.min(range.start.col, range.end.col);
  const maxCol = Math.max(range.start.col, range.end.col);

  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      cells.push({ row, col });
    }
  }
  return cells;
}

// ─────────────────────────────────────────────────────────────────────────────
// Value Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Gets numeric value from cell, returns NaN if not a number
 */
function getNumericValue(value: string): number {
  if (!value || value.trim() === "") return NaN;
  // Remove formatting (commas, $, %)
  const cleaned = value.replace(/[$,]/g, "").replace(/%$/, "");
  const num = parseFloat(cleaned);
  // If it was a percentage, convert (50% -> 0.5)
  if (value.endsWith("%") && !isNaN(num)) {
    return num / 100;
  }
  return num;
}

/**
 * Gets array of numeric values from range
 * Now evaluates formulas in cells to get their resolved values
 */
function getNumericValues(
  range: CellRange,
  getCell: CellGetter,
  liveCache?: Map<string, number | null>
): number[] {
  const cells = getCellsInRange(range);
  const values: number[] = [];
  for (const cell of cells) {
    const raw = getCell(cell.row, cell.col);

    // If it's a formula, evaluate it to get the resolved value
    if (raw.startsWith("=")) {
      const result = evaluateFormula(raw, getCell, new Set(), liveCache);
      // Skip if still loading or has error
      if (result.isLoading || result.error) continue;
      const num = typeof result.value === "number"
        ? result.value
        : getNumericValue(String(result.value));
      if (!isNaN(num)) {
        values.push(num);
      }
      continue;
    }

    const num = getNumericValue(raw);
    if (!isNaN(num)) {
      values.push(num);
    }
  }
  return values;
}

// ─────────────────────────────────────────────────────────────────────────────
// Built-in Functions
// ─────────────────────────────────────────────────────────────────────────────

type FormulaFunction = (
  args: string[],
  getCell: CellGetter,
  liveCache?: Map<string, number | null>
) => FormulaResult;

const FUNCTIONS: Record<string, FormulaFunction> = {
  // Aggregation functions
  SUM: (args, getCell, liveCache) => {
    let total = 0;
    for (const arg of args) {
      const range = parseRange(arg);
      if (range) {
        const values = getNumericValues(range, getCell, liveCache);
        total += values.reduce((a, b) => a + b, 0);
      } else {
        const num = getNumericValue(arg);
        if (!isNaN(num)) total += num;
      }
    }
    return { value: total };
  },

  AVERAGE: (args, getCell, liveCache) => {
    const allValues: number[] = [];
    for (const arg of args) {
      const range = parseRange(arg);
      if (range) {
        allValues.push(...getNumericValues(range, getCell, liveCache));
      } else {
        const num = getNumericValue(arg);
        if (!isNaN(num)) allValues.push(num);
      }
    }
    if (allValues.length === 0) return { value: 0 };
    return { value: allValues.reduce((a, b) => a + b, 0) / allValues.length };
  },

  MIN: (args, getCell, liveCache) => {
    const allValues: number[] = [];
    for (const arg of args) {
      const range = parseRange(arg);
      if (range) {
        allValues.push(...getNumericValues(range, getCell, liveCache));
      } else {
        const num = getNumericValue(arg);
        if (!isNaN(num)) allValues.push(num);
      }
    }
    if (allValues.length === 0) return { value: 0 };
    return { value: Math.min(...allValues) };
  },

  MAX: (args, getCell, liveCache) => {
    const allValues: number[] = [];
    for (const arg of args) {
      const range = parseRange(arg);
      if (range) {
        allValues.push(...getNumericValues(range, getCell, liveCache));
      } else {
        const num = getNumericValue(arg);
        if (!isNaN(num)) allValues.push(num);
      }
    }
    if (allValues.length === 0) return { value: 0 };
    return { value: Math.max(...allValues) };
  },

  COUNT: (args, getCell, liveCache) => {
    let count = 0;
    for (const arg of args) {
      const range = parseRange(arg);
      if (range) {
        count += getNumericValues(range, getCell, liveCache).length;
      } else {
        const num = getNumericValue(arg);
        if (!isNaN(num)) count++;
      }
    }
    return { value: count };
  },

  COUNTA: (args, getCell) => {
    let count = 0;
    for (const arg of args) {
      const range = parseRange(arg);
      if (range) {
        const cells = getCellsInRange(range);
        for (const cell of cells) {
          const val = getCell(cell.row, cell.col);
          if (val && val.trim() !== "") count++;
        }
      } else if (arg && arg.trim() !== "") {
        count++;
      }
    }
    return { value: count };
  },

  // Math functions
  ABS: (args) => {
    const num = getNumericValue(args[0] ?? "");
    if (isNaN(num)) return { value: "#VALUE!", error: "Invalid number" };
    return { value: Math.abs(num) };
  },

  ROUND: (args) => {
    const num = getNumericValue(args[0] ?? "");
    const decimals = parseInt(args[1] ?? "0", 10);
    if (isNaN(num)) return { value: "#VALUE!", error: "Invalid number" };
    const factor = Math.pow(10, decimals);
    return { value: Math.round(num * factor) / factor };
  },

  FLOOR: (args) => {
    const num = getNumericValue(args[0] ?? "");
    if (isNaN(num)) return { value: "#VALUE!", error: "Invalid number" };
    return { value: Math.floor(num) };
  },

  CEIL: (args) => {
    const num = getNumericValue(args[0] ?? "");
    if (isNaN(num)) return { value: "#VALUE!", error: "Invalid number" };
    return { value: Math.ceil(num) };
  },

  // Logical functions
  IF: (args) => {
    const condition = args[0];
    const trueValue = args[1] ?? "";
    const falseValue = args[2] ?? "";

    // Evaluate simple comparisons
    let result = false;
    const compMatch = condition?.match(/^(.+?)(>=|<=|<>|!=|>|<|=)(.+)$/);
    if (compMatch && compMatch[1] && compMatch[2] && compMatch[3]) {
      const left = getNumericValue(compMatch[1].trim());
      const op = compMatch[2];
      const right = getNumericValue(compMatch[3].trim());

      if (!isNaN(left) && !isNaN(right)) {
        switch (op) {
          case ">":
            result = left > right;
            break;
          case "<":
            result = left < right;
            break;
          case ">=":
            result = left >= right;
            break;
          case "<=":
            result = left <= right;
            break;
          case "=":
            result = left === right;
            break;
          case "<>":
          case "!=":
            result = left !== right;
            break;
        }
      }
    } else {
      // Boolean-like evaluation
      const num = getNumericValue(condition ?? "");
      result = !isNaN(num) && num !== 0;
    }

    return { value: result ? trueValue : falseValue };
  },

  // Text functions
  CONCAT: (args) => {
    return { value: args.join("") };
  },

  LEN: (args) => {
    return { value: (args[0] ?? "").length };
  },

  UPPER: (args) => {
    return { value: (args[0] ?? "").toUpperCase() };
  },

  LOWER: (args) => {
    return { value: (args[0] ?? "").toLowerCase() };
  },

  // Percentage/ratio helpers
  PCT: (args) => {
    const num = getNumericValue(args[0] ?? "");
    const total = getNumericValue(args[1] ?? "");
    if (isNaN(num) || isNaN(total) || total === 0) {
      return { value: "#DIV/0!", error: "Division by zero" };
    }
    return { value: (num / total) * 100 };
  },

  CHANGE: (args) => {
    const newVal = getNumericValue(args[0] ?? "");
    const oldVal = getNumericValue(args[1] ?? "");
    if (isNaN(newVal) || isNaN(oldVal) || oldVal === 0) {
      return { value: "#DIV/0!", error: "Division by zero" };
    }
    return { value: ((newVal - oldVal) / Math.abs(oldVal)) * 100 };
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Expression Parser (replaces eval)
// ─────────────────────────────────────────────────────────────────────────────

type Token =
  | { type: "number"; value: number }
  | { type: "operator"; value: string }
  | { type: "paren"; value: "(" | ")" };

function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < expr.length) {
    const char = expr[i];

    // Skip whitespace
    if (char === " " || char === "\t") {
      i++;
      continue;
    }

    // Number (including decimals and negative)
    if (/[\d.]/.test(char ?? "") || (char === "-" && (tokens.length === 0 || tokens[tokens.length - 1]?.type === "operator" || tokens[tokens.length - 1]?.type === "paren"))) {
      let numStr = "";
      if (char === "-") {
        numStr = "-";
        i++;
      }
      while (i < expr.length && /[\d.]/.test(expr[i] ?? "")) {
        numStr += expr[i];
        i++;
      }
      tokens.push({ type: "number", value: parseFloat(numStr) });
      continue;
    }

    // Operators
    if (char && "+-*/%^".includes(char)) {
      tokens.push({ type: "operator", value: char });
      i++;
      continue;
    }

    // Parentheses
    if (char === "(" || char === ")") {
      tokens.push({ type: "paren", value: char });
      i++;
      continue;
    }

    // Unknown character - skip
    i++;
  }

  return tokens;
}

function evaluateTokens(tokens: Token[]): number {
  if (tokens.length === 0) return 0;

  // Handle parentheses first
  let i = 0;
  while (i < tokens.length) {
    if (tokens[i]?.type === "paren" && tokens[i]?.value === "(") {
      // Find matching closing paren
      let depth = 1;
      let j = i + 1;
      while (j < tokens.length && depth > 0) {
        if (tokens[j]?.type === "paren" && tokens[j]?.value === "(") depth++;
        if (tokens[j]?.type === "paren" && tokens[j]?.value === ")") depth--;
        j++;
      }
      // Evaluate sub-expression
      const subResult = evaluateTokens(tokens.slice(i + 1, j - 1));
      tokens.splice(i, j - i, { type: "number", value: subResult });
    } else {
      i++;
    }
  }

  // Handle ^ (power) - right to left
  for (let k = tokens.length - 1; k >= 0; k--) {
    if (tokens[k]?.type === "operator" && tokens[k]?.value === "^") {
      const left = tokens[k - 1];
      const right = tokens[k + 1];
      if (left?.type === "number" && right?.type === "number") {
        const result = Math.pow(left.value, right.value);
        tokens.splice(k - 1, 3, { type: "number", value: result });
      }
    }
  }

  // Handle * / %
  for (let k = 0; k < tokens.length; k++) {
    const token = tokens[k];
    if (token?.type === "operator" && (token.value === "*" || token.value === "/" || token.value === "%")) {
      const left = tokens[k - 1];
      const right = tokens[k + 1];
      if (left?.type === "number" && right?.type === "number") {
        let result: number;
        if (token.value === "*") result = left.value * right.value;
        else if (token.value === "/") result = right.value !== 0 ? left.value / right.value : NaN;
        else result = right.value !== 0 ? left.value % right.value : NaN;
        tokens.splice(k - 1, 3, { type: "number", value: result });
        k--;
      }
    }
  }

  // Handle + -
  for (let k = 0; k < tokens.length; k++) {
    const token = tokens[k];
    if (token?.type === "operator" && (token.value === "+" || token.value === "-")) {
      const left = tokens[k - 1];
      const right = tokens[k + 1];
      if (left?.type === "number" && right?.type === "number") {
        const result = token.value === "+" ? left.value + right.value : left.value - right.value;
        tokens.splice(k - 1, 3, { type: "number", value: result });
        k--;
      }
    }
  }

  const final = tokens[0];
  return final?.type === "number" ? final.value : NaN;
}

function evaluateArithmetic(expr: string): number {
  const tokens = tokenize(expr);
  return evaluateTokens(tokens);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Formula Evaluator
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse function call arguments, handling nested functions and ranges
 */
function parseArguments(argsStr: string): string[] {
  const args: string[] = [];
  let current = "";
  let depth = 0;

  for (const char of argsStr) {
    if (char === "(") {
      depth++;
      current += char;
    } else if (char === ")") {
      depth--;
      current += char;
    } else if (char === "," && depth === 0) {
      args.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    args.push(current.trim());
  }

  return args;
}

/**
 * Check if a formula contains LIVE() function
 */
export function containsLiveFormula(formula: string): boolean {
  return /LIVE\s*\(/i.test(formula);
}

/**
 * Main formula evaluation function (synchronous)
 * For LIVE() formulas, uses the provided liveCache for resolved values
 */
export function evaluateFormula(
  formula: string,
  getCell: CellGetter,
  visitedCells: Set<string> = new Set(),
  liveCache?: Map<string, number | null>
): FormulaResult {
  try {
    // Remove leading =
    let expr = formula.startsWith("=") ? formula.slice(1).trim() : formula.trim();

    if (!expr) return { value: "" };

    // Check for function call: FUNC(args)
    const funcMatch = expr.match(/^([A-Z_]+)\((.+)\)$/i);
    if (funcMatch && funcMatch[1] && funcMatch[2]) {
      const funcName = funcMatch[1].toUpperCase();
      const argsStr = funcMatch[2];

      // Handle LIVE() function specially
      if (funcName === "LIVE") {
        // First, resolve any cell references in the arguments
        // Parse args: LIVE(A1, "price") or LIVE("BTC", "price") or LIVE(A1, B1)
        const liveArgs = parseArguments(argsStr);
        if (liveArgs.length < 2) {
          return { value: "#ERROR", error: "LIVE requires 2 arguments: ticker and metric" };
        }

        // Resolve first argument (ticker) - could be cell ref or string literal
        let ticker = liveArgs[0] || "";
        const tickerCellRef = parseCellRef(ticker);
        if (tickerCellRef) {
          // It's a cell reference - get the value from that cell
          ticker = getCell(tickerCellRef.row, tickerCellRef.col);
          // If the referenced cell has a formula, evaluate it
          if (ticker.startsWith("=")) {
            const result = evaluateFormula(ticker, getCell, new Set(visitedCells), liveCache);
            ticker = String(result.value);
          }
        } else {
          // Remove quotes if it's a string literal
          ticker = ticker.replace(/^["']|["']$/g, "");
        }

        // Resolve second argument (metric) - could be cell ref or string literal
        let metric = liveArgs[1] || "";
        const metricCellRef = parseCellRef(metric);
        if (metricCellRef) {
          metric = getCell(metricCellRef.row, metricCellRef.col);
          if (metric.startsWith("=")) {
            const result = evaluateFormula(metric, getCell, new Set(visitedCells), liveCache);
            metric = String(result.value);
          }
        } else {
          metric = metric.replace(/^["']|["']$/g, "");
        }

        if (!ticker || !metric) {
          return { value: "#ERROR", error: "LIVE requires ticker and metric" };
        }

        // Normalize ticker to uppercase
        ticker = ticker.toUpperCase().trim();
        metric = metric.toLowerCase().trim();

        // Check cache
        const cacheKey = `${ticker}:${metric}`;
        if (liveCache?.has(cacheKey)) {
          const cachedValue = liveCache.get(cacheKey);
          if (cachedValue !== null && cachedValue !== undefined) {
            return { value: cachedValue };
          }
          return { value: "#N/A" };
        }

        // Not in cache - return loading indicator
        return { value: "...", isLoading: true };
      }

      // Parse arguments
      const args = parseArguments(argsStr);

      // Evaluate each argument (resolve cell refs and nested formulas)
      const evaluatedArgs = args.map((arg) => {
        // If it's a range like A1:B5, keep as-is for range functions
        if (parseRange(arg)) return arg;

        // If it's a cell ref, get the value
        const cellRef = parseCellRef(arg);
        if (cellRef) {
          const cellKey = `${cellRef.row},${cellRef.col}`;
          if (visitedCells.has(cellKey)) {
            return "#CIRCULAR!";
          }
          const cellValue = getCell(cellRef.row, cellRef.col);
          if (cellValue.startsWith("=")) {
            visitedCells.add(cellKey);
            const result = evaluateFormula(cellValue, getCell, visitedCells, liveCache);
            return String(result.value);
          }
          return cellValue;
        }

        // If it's a nested formula, evaluate it
        if (arg.includes("(")) {
          const result = evaluateFormula("=" + arg, getCell, visitedCells, liveCache);
          return String(result.value);
        }

        return arg;
      });

      // Execute function
      const func = FUNCTIONS[funcName];
      if (func) {
        return func(evaluatedArgs, getCell, liveCache);
      }

      return { value: "#NAME?", error: `Unknown function: ${funcName}` };
    }

    // Not a function - evaluate as arithmetic expression
    // First resolve cell references
    expr = expr.replace(/([A-Z]+)(\d+)/gi, (match) => {
      const cellRef = parseCellRef(match);
      if (!cellRef) return "0";

      const cellKey = `${cellRef.row},${cellRef.col}`;
      if (visitedCells.has(cellKey)) {
        return "0"; // Circular ref
      }

      const cellValue = getCell(cellRef.row, cellRef.col);
      if (cellValue.startsWith("=")) {
        visitedCells.add(cellKey);
        const result = evaluateFormula(cellValue, getCell, visitedCells);
        const num = parseFloat(String(result.value));
        return isNaN(num) ? "0" : String(num);
      }

      const num = getNumericValue(cellValue);
      return isNaN(num) ? "0" : String(num);
    });

    // Evaluate arithmetic
    const result = evaluateArithmetic(expr);
    if (isNaN(result)) {
      return { value: "#ERROR", error: "Invalid expression" };
    }

    return { value: result };
  } catch (err) {
    return { value: "#ERROR", error: String(err) };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Detects if a cell value is likely a number
 */
export function isNumericValue(value: string): boolean {
  if (!value || value.trim() === "") return false;
  const cleaned = value.replace(/[$,]/g, "").replace(/%$/, "");
  return !isNaN(parseFloat(cleaned));
}

/**
 * Extracts all LIVE() formulas from spreadsheet data for batch fetching
 * Resolves cell references to get actual ticker/metric values
 */
export function extractLiveFormulas(data: string[][]): Array<{ row: number; col: number; ticker: string; metric: string }> {
  const liveFormulas: Array<{ row: number; col: number; ticker: string; metric: string }> = [];

  // Helper to get cell value (for resolving references)
  const getCellValue = (r: number, c: number): string => {
    return data[r]?.[c] || "";
  };

  for (let row = 0; row < data.length; row++) {
    const rowData = data[row];
    if (!rowData) continue;
    for (let col = 0; col < rowData.length; col++) {
      const cell = rowData[col];
      if (cell && containsLiveFormula(cell)) {
        const match = cell.match(/LIVE\s*\(\s*(.+)\s*\)/i);
        if (match && match[1]) {
          const args = parseArguments(match[1]);
          if (args.length >= 2) {
            // Resolve ticker (first arg)
            let ticker = args[0] || "";
            const tickerRef = parseCellRef(ticker);
            if (tickerRef) {
              ticker = getCellValue(tickerRef.row, tickerRef.col);
            } else {
              ticker = ticker.replace(/^["']|["']$/g, "");
            }

            // Resolve metric (second arg)
            let metric = args[1] || "";
            const metricRef = parseCellRef(metric);
            if (metricRef) {
              metric = getCellValue(metricRef.row, metricRef.col);
            } else {
              metric = metric.replace(/^["']|["']$/g, "");
            }

            if (ticker && metric) {
              liveFormulas.push({
                row,
                col,
                ticker: ticker.toUpperCase().trim(),
                metric: metric.toLowerCase().trim(),
              });
            }
          }
        }
      }
    }
  }

  return liveFormulas;
}
